/**
 * Constant-velocity Kalman filter for 2D position and velocity.
 * State: [x, y, vx, vy]
 */
export class KalmanFilter2D {
  private state: [number, number, number, number];
  private P: number[][]; // 4x4 covariance
  private readonly dt: number;
  private readonly processNoise: number;
  private readonly measurementNoise: number;

  constructor(
    x: number,
    y: number,
    options?: {
      dt?: number;
      processNoise?: number;
      measurementNoise?: number;
    }
  ) {
    this.dt = options?.dt ?? 1;
    this.processNoise = options?.processNoise ?? 0.1;
    this.measurementNoise = options?.measurementNoise ?? 1;
    this.state = [x, y, 0, 0];
    this.P = this.identity4();
  }

  private identity4(): number[][] {
    return [
      [1, 0, 0, 0],
      [0, 1, 0, 0],
      [0, 0, 1, 0],
      [0, 0, 0, 1],
    ];
  }

  predict(): [number, number, number, number] {
    const [x, y, vx, vy] = this.state;
    this.state = [
      x + vx * this.dt,
      y + vy * this.dt,
      vx,
      vy,
    ];
    const F = [
      [1, 0, this.dt, 0],
      [0, 1, 0, this.dt],
      [0, 0, 1, 0],
      [0, 0, 0, 1],
    ];
    const I = this.identity4();
    const Q = I.map((row) => row.map((v) => v * this.processNoise));
    this.P = this.add(this.mmul(this.mmul(F, this.P), this.transpose(F)), Q);
    return [...this.state];
  }

  update(zx: number, zy: number): [number, number, number, number] {
    const H = [
      [1, 0, 0, 0],
      [0, 1, 0, 0],
    ];
    const R = [
      [this.measurementNoise, 0],
      [0, this.measurementNoise],
    ];
    const z = [zx, zy];
    const Ht = this.transpose(H);
    const S = this.add(this.mmul(this.mmul(H, this.P), Ht), R);
    const K = this.mmul(this.mmul(this.P, Ht), this.inv2x2(S));
    const y = [z[0] - this.state[0], z[1] - this.state[1]];
    const Ky = [K[0][0] * y[0] + K[0][1] * y[1], K[1][0] * y[0] + K[1][1] * y[1]];
    this.state = [
      this.state[0] + Ky[0],
      this.state[1] + Ky[1],
      this.state[2] + (K[2][0] * y[0] + K[2][1] * y[1]),
      this.state[3] + (K[3][0] * y[0] + K[3][1] * y[1]),
    ];
    const I_KH = this.sub(this.identity4(), this.mmul(K, H));
    this.P = this.mmul(this.mmul(I_KH, this.P), this.transpose(I_KH));
    return [...this.state];
  }

  getState(): [number, number, number, number] {
    return [...this.state];
  }

  getPosition(): [number, number] {
    return [this.state[0], this.state[1]];
  }

  getVelocity(): [number, number] {
    return [this.state[2], this.state[3]];
  }

  private mmul(a: number[][], b: number[][]): number[][] {
    const rows = a.length;
    const cols = b[0].length;
    const inner = b.length;
    const out: number[][] = [];
    for (let i = 0; i < rows; i++) {
      out[i] = [];
      for (let j = 0; j < cols; j++) {
        let s = 0;
        for (let k = 0; k < inner; k++) s += a[i][k] * b[k][j];
        out[i][j] = s;
      }
    }
    return out;
  }

  private transpose(m: number[][]): number[][] {
    const r = m.length;
    const c = m[0].length;
    const out: number[][] = [];
    for (let j = 0; j < c; j++) {
      out[j] = [];
      for (let i = 0; i < r; i++) out[j][i] = m[i][j];
    }
    return out;
  }

  private add(a: number[][], b: number[][]): number[][] {
    return a.map((row, i) => row.map((v, j) => v + b[i][j]));
  }

  private sub(a: number[][], b: number[][]): number[][] {
    return a.map((row, i) => row.map((v, j) => v - b[i][j]));
  }

  private inv2x2(m: number[][]): number[][] {
    const [[a, b], [c, d]] = m;
    const det = a * d - b * c;
    const invDet = 1 / (det || 1e-10);
    return [
      [d * invDet, -b * invDet],
      [-c * invDet, a * invDet],
    ];
  }
}
